// ========= IMPORTS ==========


import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageProvider";


// ========= MAIN COMPONENT ==========

const IMGI2 = () => {
    const navigate = useNavigate();           // For navigating to the next task step
    const { translation } = useLanguage();    // Translation function

    // ====== State Variables ======
    const [selectedColors, setSelectedColors] = useState([]); // Up to 3 chosen colors
    const [errorMessage, setErrorMessage] = useState("");     // Error feedback if incorrect selection count

    // Fetch translated color list from translations
    // The key "Colors" holds all color names in a single string separated by "/" (e.g., "Red/Green/Blue...")
    const translatedColors = translation("Colors");
    const colors = translatedColors.split('/'); // Convert to an array of strings

    // ====== Keyboard Listener ======
    const handleKeyPress = (event) => {
        if (event.key === " ") { // Spacebar triggers progression
            if (selectedColors.length !== 3) {
                // Show translated error prompt if user hasn’t chosen exactly 3 colors
                setErrorMessage(translation("IMGI2_numberSelectionPrompt"));
            } else {
                // Derive list of unselected colors from full list
                const unSelectedColors = colors.filter(
                    color => !selectedColors.includes(color)
                );

                // Navigate to next screen "/IMG2", passing both selected and unselected colors in router state
                navigate("/IMG2", { 
                    state: { selectedColors, unSelectedColors }
                });
            }
        }
    };

    // ====== Checkbox Click Handler ======
    const handleCheckboxChange = (color) => {
        setSelectedColors((prevSelected) => {
            if (prevSelected.includes(color)) {
                // Already selected → deselect it
                return prevSelected.filter((c) => c !== color);
            } else if (prevSelected.length < 3) {
                // Not selected and under 3 selections → select it
                return [...prevSelected, color];
            }
            // Already at 3 selections → ignore additional clicks unless it was already selected
            return prevSelected;
        });
    };

    // ====== Side Effect: Keyboard Bindings ======
    useEffect(() => {
        // Attach handler to global keydown event
        window.addEventListener("keydown", handleKeyPress);

        // Cleanup on unmount or update (removes stale handler)
        return () => {
            window.removeEventListener("keydown", handleKeyPress);
        };
    // Include selectedColors so handler logic always has up‑to‑date selection counts
    }, [selectedColors]);

    // ====== Render ======
    return (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
           <div className="flex flex-col items-center justify-center relative">

                {/* Heading: congratulatory / intro message */}
                <h1 className="text-4xl text-gray-800 mb-5 text-left w-full max-w-5xl ml-4">
                    {translation("IMGI2_congratulationsMessage")}
                </h1>

                {/* Heading: test instructions */}
                <h2 className="text-4xl text-gray-800 mb-8 text-left w-full max-w-5xl ml-4">
                    {translation("IMGI2_testInstructions")}
                </h2>

                {/* Main instructions and selection UI */}
                <ul className="list-disc mb-6 w-full max-w-5xl ml-16 pl-2 text-left text-2xl">

                    {/* Step 1: Choose favourite colors
                       Using dangerouslySetInnerHTML as prompt might embed HTML tags (<b>, <i>, links, spans…) from translations.
                    */}
                    <li
                        className="mb-2"
                        dangerouslySetInnerHTML={{
                            __html: translation("IMGI2_favoriteColorssPrompt")
                        }}
                    />

                    {/* Color choices grid — 2 columns */}
                    <div className="grid grid-cols-2 gap-4">
                        {colors.map((color, index) => (
                            <label
                                key={index}
                                className="flex items-center text-2xl font-medium"
                            >
                                <input
                                    type="checkbox"
                                    value={color}
                                    checked={selectedColors.includes(color)}
                                    onChange={() => handleCheckboxChange(color)}
                                    // Disable further selections beyond 3 unless it's already checked
                                    disabled={
                                        selectedColors.length >= 3 &&
                                        !selectedColors.includes(color)
                                    }
                                    className="mr-2"
                                />
                                {color}
                            </label>
                        ))}
                    </div>

                    {/* Extra instructional list items */}
                    <li className="mb-2">
                        {translation("IMGI2_ColorsAppearancePrompt")}
                    </li>
                    <li className="mb-2">
                        {translation("IMGI2_taskInstructions")}
                    </li>
                    <li className="mb-2">
                        {translation("IMGI2_noPressInstruction")}
                    </li>
                    <li className="mb-2">
                        {translation("IMGI2_speedAccuracyReminder")}
                    </li>
                    <li className="mb-2">
                        {translation("IMGI2_warningResponseSpeed")}
                    </li>
                </ul>

                {/* Final begin prompt */}
                <h2 className="text-3xl text-gray-800 mb-8 text-left w-full max-w-5xl ml-4">
                    {translation("IMGI2_beginPrompt")}
                </h2>

                {/* Error message if selection invalid when attempting to start */}
                {errorMessage && (
                    <span className="absolute -bottom-5 text-red-600 text-2xl font-bold">
                        {errorMessage}
                    </span>
                )}
            </div>
        </div>
    );
};

export default IMGI2;
